import type { GameCode } from '../../entities/room/types';

/**
 * The player-visible name of a bet option.
 *
 * `label` is an English authoring fallback ('Odd', 'Big', 'Any Double') — it is
 * never what a player should read. Anything that puts an option's name on screen
 * or carries it into another screen must go through here, or the English leaks:
 * the bet-success sheet used to announce a Portuguese bet as `em 「Odd」`.
 */
export function optionLabel(option: { label: string; labelKey?: string }, t: (key: any) => string): string {
  return option.labelKey ? t(option.labelKey) : option.label;
}

export type BetOption = {
  tab: string;
  pick: string;
  label: string;
  /** i18n key for the visible label (legacy t('odd')/t('big')…); falls back to label. */
  labelKey?: string;
  /** i18n key for the sublabel; falls back to the literal `sublabel`. */
  sublabelKey?: string;
  sublabel?: string;
  /** payout line, e.g. '×2' / '×1.95' / '×62' (legacy brand-colored mono line). */
  odds?: string;
  /** boundary/tier note, e.g. '9 → ×1.5'; noteKey renders a translated note with alert icon. */
  note?: string;
  noteKey?: string;
  /** dice faces to render as pip SVGs instead of a text label. */
  dice?: number[];
  tone?: 'gold' | 'green' | 'red' | 'blue' | 'violet' | 'dark' | 'rose' | 'cyan';
};

/** Legacy per-sum payout table (game-globals.js sicboSumOdds). */
export const SICBO_SUM_ODDS: Record<number, number> = { 4: 62, 17: 62, 5: 31, 16: 31, 6: 18, 15: 18, 7: 13, 14: 13, 8: 9, 13: 9, 9: 8, 12: 8, 10: 7, 11: 7 };

/**
 * The tab keys BET_TABS actually defines.
 *
 * Spelled out as a union rather than left as `string` because a bet target is
 * resolved by name at runtime: `findBetOption` returns null for a key that does
 * not exist, and a null option silently turns a tap target back into plain
 * text. A misspelling used to be a compile-time non-event and a runtime dead
 * button — a global rename of `sicbo-sum` once took out the Missing tab's sum
 * tiles, the Sum tab's how-to example and its long-press hint in one go, and
 * nothing failed until someone tried to tap. Now it fails here.
 */
export type BetTabKey =
  | 'odd-even' | 'big-small' | 'specific'
  | 'sicbo-bs' | 'sicbo-sum' | 'sicbo-single' | 'sicbo-combo' | 'sicbo-pairs'
  | 'roulette-num' | 'roulette-rb' | 'roulette-oe' | 'roulette-hilo'
  | 'roulette-dozen' | 'roulette-column' | 'roulette-split' | 'roulette-street'
  | 'roulette-corner' | 'roulette-line';

export type BetTab = {
  key: BetTabKey;
  label: string;
  options: BetOption[];
};

/**
 * NOT for display. English authoring names, useful only as a membership test
 * ("is this a known game code?"). Anything a player reads must use
 * `t(gameNameKey(game))`, or the lobby says "Último Dígito" while the bet
 * history says "Last Digit" for the very same game.
 */
export const GAME_LABELS: Record<GameCode, string> = {
  lastdigit: 'Last Digit',
  sicbo: 'Sicbo',
  roulette: 'Roulette',
};

export const BET_TABS: Record<GameCode, BetTab[]> = {
  lastdigit: [
    {
      key: 'odd-even',
      label: 'Odd / Even',
      options: [
        { tab: 'odd-even', pick: 'odd', label: 'Odd', labelKey: 'odd', sublabel: '1·3·5·7·9', odds: '×2', note: '9 → ×1.5', tone: 'rose' },
        { tab: 'odd-even', pick: 'even', label: 'Even', labelKey: 'even', sublabel: '0·2·4·6·8', odds: '×2', note: '0 → ×1.5', tone: 'blue' },
      ],
    },
    {
      key: 'big-small',
      label: 'Big / Small',
      options: [
        { tab: 'big-small', pick: 'small', label: 'Small', labelKey: 'small', sublabel: '0–4', odds: '×2', note: '0 → ×1.5', tone: 'cyan' },
        { tab: 'big-small', pick: 'big', label: 'Big', labelKey: 'big', sublabel: '5–9', odds: '×2', note: '9 → ×1.5', tone: 'gold' },
      ],
    },
    {
      key: 'specific',
      label: 'Digits',
      options: Array.from({ length: 10 }, (_, n) => ({
        tab: 'specific',
        pick: String(n),
        label: String(n),
        tone: 'violet' as const,
      })),
    },
  ],
  sicbo: [
    {
      key: 'sicbo-bs',
      label: 'Big / Small',
      options: [
        { tab: 'sicbo-bs', pick: 'small', label: 'Small', labelKey: 'small', sublabel: '4 – 10', odds: '×1.95', noteKey: 'sicboBsTripleNote', tone: 'cyan' },
        { tab: 'sicbo-bs', pick: 'big', label: 'Big', labelKey: 'big', sublabel: '11 – 17', odds: '×1.95', noteKey: 'sicboBsTripleNote', tone: 'gold' },
      ],
    },
    {
      key: 'sicbo-sum',
      label: 'Sum',
      options: Array.from({ length: 14 }, (_, i) => {
        const sum = i + 4;
        return { tab: 'sicbo-sum', pick: `s${sum}`, label: String(sum), odds: `×${SICBO_SUM_ODDS[sum]}`, tone: 'gold' as const };
      }),
    },
    {
      key: 'sicbo-single',
      label: 'Single',
      options: Array.from({ length: 6 }, (_, i) => {
        const die = i + 1;
        return { tab: 'sicbo-single', pick: String(die), label: String(die), dice: [die], tone: 'blue' as const };
      }),
    },
    {
      key: 'sicbo-combo',
      label: 'Combo',
      options: sicboCombos().map(([a, b]) => ({
        tab: 'sicbo-combo',
        pick: `c${a}${b}`,
        label: `${a}-${b}`,
        dice: [a, b],
        tone: 'violet' as const,
      })),
    },
    {
      key: 'sicbo-pairs',
      label: 'Pairs',
      options: [
        { tab: 'sicbo-pairs', pick: 'ad', label: 'Any Double', labelKey: 'sicboAnyDouble', odds: '×2.2', tone: 'gold' as const },
        { tab: 'sicbo-pairs', pick: 'any', label: 'Any Triple', labelKey: 'sicboAnyTriple', odds: '×30', tone: 'violet' as const },
        ...Array.from({ length: 6 }, (_, i) => {
          const die = i + 1;
          return { tab: 'sicbo-pairs', pick: `d${die}`, label: `Double ${die}`, dice: [die, die], odds: '×12', tone: 'blue' as const };
        }),
        ...Array.from({ length: 6 }, (_, i) => {
          const die = i + 1;
          return { tab: 'sicbo-pairs', pick: `t${die}`, label: `Triple ${die}`, dice: [die, die, die], odds: '×180', tone: 'violet' as const };
        }),
      ],
    },
  ],
  roulette: [
    {
      key: 'roulette-num',
      label: 'Number',
      options: Array.from({ length: 37 }, (_, n) => ({
        tab: 'roulette-num',
        pick: String(n),
        label: String(n),
        sublabelKey: n === 0 ? 'roulGreen' : undefined,
        sublabel: n === 0 ? 'verde' : '×35',
        tone: n === 0 ? 'green' : rouletteTone(n),
      })),
    },
    {
      key: 'roulette-rb',
      label: 'Color',
      options: [
        { tab: 'roulette-rb', pick: 'red', label: 'Red', labelKey: 'red', sublabelKey: 'roulSub18', tone: 'red' },
        { tab: 'roulette-rb', pick: 'black', label: 'Black', labelKey: 'black', sublabelKey: 'roulSub18', tone: 'dark' },
      ],
    },
    {
      key: 'roulette-oe',
      label: 'Odd / Even',
      options: [
        { tab: 'roulette-oe', pick: 'odd', label: 'Odd', labelKey: 'odd', sublabelKey: 'roulSub18', tone: 'rose' },
        { tab: 'roulette-oe', pick: 'even', label: 'Even', labelKey: 'even', sublabelKey: 'roulSub18', tone: 'blue' },
      ],
    },
    {
      key: 'roulette-hilo',
      label: 'Low / High',
      options: [
        { tab: 'roulette-hilo', pick: 'lo', label: '1 – 18', sublabelKey: 'roulSub18', tone: 'green' },
        { tab: 'roulette-hilo', pick: 'hi', label: '19 – 36', sublabelKey: 'roulSub18', tone: 'red' },
      ],
    },
    {
      key: 'roulette-dozen',
      label: 'Dozen',
      options: [
        { tab: 'roulette-dozen', pick: 'dz1', label: '1st 12', labelKey: 'roulDozen_dz1', sublabel: '1–12', tone: 'violet' },
        { tab: 'roulette-dozen', pick: 'dz2', label: '2nd 12', labelKey: 'roulDozen_dz2', sublabel: '13–24', tone: 'violet' },
        { tab: 'roulette-dozen', pick: 'dz3', label: '3rd 12', labelKey: 'roulDozen_dz3', sublabel: '25–36', tone: 'violet' },
      ],
    },
    {
      key: 'roulette-column',
      label: 'Column',
      options: [
        { tab: 'roulette-column', pick: 'col1', label: 'Column 1', labelKey: 'roulColumn_col1', sublabel: '×3', tone: 'blue' },
        { tab: 'roulette-column', pick: 'col2', label: 'Column 2', labelKey: 'roulColumn_col2', sublabel: '×3', tone: 'blue' },
        { tab: 'roulette-column', pick: 'col3', label: 'Column 3', labelKey: 'roulColumn_col3', sublabel: '×3', tone: 'blue' },
      ],
    },
    {
      key: 'roulette-split',
      label: 'Split',
      options: rouletteSplits().map(([a, b]) => ({
        tab: 'roulette-split',
        pick: `sp_${a}_${b}`,
        label: `${a}-${b}`,
        sublabel: '×17',
        tone: a === 0 ? 'green' as const : 'gold' as const,
      })),
    },
    {
      key: 'roulette-street',
      label: 'Street',
      options: Array.from({ length: 12 }, (_, i) => {
        const start = i * 3 + 1;
        return {
          tab: 'roulette-street',
          pick: `st${i + 1}`,
          label: `${start}-${start + 2}`,
          sublabel: 'x11',
          tone: 'violet' as const,
        };
      }),
    },
    {
      key: 'roulette-corner',
      label: 'Corner',
      options: rouletteCorners().map((topLeft) => ({
        tab: 'roulette-corner',
        pick: `cn${topLeft}`,
        label: rouletteCornerNumbers(topLeft).join('-'),
        sublabel: 'x8',
        tone: 'blue' as const,
      })),
    },
    {
      key: 'roulette-line',
      label: 'Line',
      options: Array.from({ length: 11 }, (_, i) => {
        const start = i * 3 + 1;
        return {
          tab: 'roulette-line',
          pick: `ln${i + 1}`,
          label: `${start}-${start + 5}`,
          sublabel: 'x6',
          tone: 'green' as const,
        };
      }),
    },
  ],
};

/**
 * A deep-link from a read-the-table tile into the bet sheet: which tab to open
 * and which option to point at. Every Missing tile counts exactly one market,
 * so the tile can hand the sheet that market's coordinates instead of making
 * the player re-find it by hand.
 */
export type BetTarget = { tab: BetTabKey; pick: string };

/**
 * Resolve a target against the real market list. A tile only becomes a bet
 * button when this returns an option — that way a counting cell can never
 * deep-link into a pick the sheet has no way to show.
 */
export function findBetOption(game: GameCode, target: BetTarget | null | undefined): BetOption | null {
  if (!target) return null;
  const tab = BET_TABS[game].find((item) => item.key === target.tab);
  return tab?.options.find((option) => option.pick === target.pick) || null;
}

/** i18n key for a game's localized display name (matches legacy game_* keys).
 *
 *  Takes a plain string because bets carry `game` untyped from the API — a bet
 *  row for a game this build doesn't know about must still render *something*
 *  honest. Falling back to a concrete game (as this did before Milhar) makes an
 *  unknown bet silently claim to be Último Dígito, which is worse than useless:
 *  the player sees the wrong game name next to the wrong draw digits and
 *  concludes we tampered with their bet. */
export function gameNameKey(
  game: string,
  tab?: string,
): 'game_lastdigit' | 'game_sicbo' | 'game_roulette' | 'game_milhar'
  | 'game_milhar_hora' | 'game_unknown' {
  if (game === 'lastdigit') return 'game_lastdigit';
  if (game === 'sicbo') return 'game_sicbo';
  if (game === 'roulette') return 'game_roulette';
  // Both Milhar tiers report game='milhar'; the program lives in `tab`
  // (MilharTier.tab()). Without it a 5-digit hourly bet sits in the history
  // labelled exactly like a 4-digit one, and the player has only the pick
  // length to tell two different games apart.
  if (game === 'milhar') return tab === 'milhar-hora' ? 'game_milhar_hora' : 'game_milhar';
  return 'game_unknown';
}

/**
 * The betting program a bet belongs to — what the player thinks of as "the game".
 *
 * For Milhar that is the tier (`milhar` / `milhar-hora`), which is finer than
 * `game`; for everything else `tab` is a bet type (odd-even, big-small…) and the
 * game itself is the program.
 */
export function betProgram(game: string, tab?: string): string {
  return game === 'milhar' && tab ? tab : game;
}

export function defaultBetTab(game: GameCode) {
  return BET_TABS[game][0].key;
}

export function tabFor(game: GameCode, key: string) {
  return BET_TABS[game].find((tab) => tab.key === key) || BET_TABS[game][0];
}

export function pickLabel(game: GameCode | string, tab: string | undefined, pick: string | number | undefined, t?: (key: any) => string) {
  const safeGame = game === 'lastdigit' || game === 'sicbo' || game === 'roulette' ? game : 'lastdigit';
  const pickText = String(pick ?? '');
  for (const betTab of BET_TABS[safeGame]) {
    if (tab && betTab.key !== tab) continue;
    const option = betTab.options.find((item) => item.pick === pickText);
    if (option) {
      if (t) {
        // Receipts and tickers read better with words than bare codes.
        if (option.labelKey) return t(option.labelKey);
        if (betTab.key === 'sicbo-sum') return `${t('sicboSumLabel')} ${option.label}`;
        const double = betTab.key === 'sicbo-pairs' ? /^d([1-6])$/.exec(pickText) : null;
        if (double) return t('sicboDoubleN').replace('%n%', double[1]);
        const tripleOf = betTab.key === 'sicbo-pairs' ? /^t([1-6])$/.exec(pickText) : null;
        if (tripleOf) return t('sicboTripleN').replace('%n%', tripleOf[1]);
      }
      return option.label;
    }
  }
  return pickText || '--';
}

function rouletteTone(n: number): BetOption['tone'] {
  const reds = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
  return reds.has(n) ? 'red' : 'dark';
}

function sicboCombos() {
  const combos: Array<[number, number]> = [];
  for (let a = 1; a <= 6; a += 1) {
    for (let b = a + 1; b <= 6; b += 1) combos.push([a, b]);
  }
  return combos;
}

function rouletteSplits() {
  const splits: Array<[number, number]> = [[0, 1], [0, 2], [0, 3]];
  for (let row = 0; row < 12; row += 1) {
    const start = row * 3 + 1;
    splits.push([start, start + 1], [start + 1, start + 2]);
  }
  for (let row = 0; row < 11; row += 1) {
    const start = row * 3 + 1;
    splits.push([start, start + 3], [start + 1, start + 4], [start + 2, start + 5]);
  }
  return splits;
}

function rouletteCorners() {
  return Array.from({ length: 11 }, (_, row) => row * 3 + 1)
    .flatMap((start) => [start, start + 1]);
}

function rouletteCornerNumbers(topLeft: number) {
  return [topLeft, topLeft + 1, topLeft + 3, topLeft + 4];
}
